import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Database, RefreshCw, Loader2, Search, TrendingUp, Wallet, PiggyBank, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ProjectDetailModal from '../components/pnl/ProjectDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_BAR = { Training: 'bg-emerald-500', Venue: 'bg-amber-500', Consultancy: 'bg-sky-500', Unclassified: 'bg-slate-400' };

const CrmProjectsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { selectedFY: globalFY } = useFinancialYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const isAdmin = user?.role === 'ADMIN';
  const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

  const fetchData = useCallback(async () => {
    if (!globalFY) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({ financial_year: globalFY });
      if (search) params.append('search', search);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      const res = await axios.get(`${API}/crm/projects?${params}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load CRM projects');
    } finally {
      setLoading(false);
    }
  }, [globalFY, search, typeFilter, getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const pollJob = async (jobId) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const res = await axios.get(`${API}/zoho/sync-jobs/${jobId}`, { headers: getAuthHeaders() });
      if (res.data.status === 'success') return res.data;
      if (res.data.status === 'failed' || res.data.status === 'error') throw new Error(res.data.error || 'Sync failed');
    }
    throw new Error('Sync timed out');
  };

  const handleSync = async () => {
    if (!isAdmin) { toast.error('Only Admin can sync'); return; }
    try {
      setSyncing(true);
      toast.info('Refetching CRM Corp Tracker…');
      const res = await axios.post(`${API}/crm/sync-corp-tracker`, {}, { headers: getAuthHeaders() });
      await pollJob(res.data.job_id);
      toast.success('CRM sync complete');
      fetchData();
    } catch (e) {
      toast.error(`Sync failed: ${e.message || 'unknown'}`);
    } finally {
      setSyncing(false);
    }
  };

  const s = data?.summary;

  return (
    <div className="space-y-6" data-testid="crm-projects-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-slate-900 text-white"><Database className="w-6 h-6" /></div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">CRM Projects</h1>
            <p className="text-sm text-slate-500">Zoho CRM Corp Tracker — FY {globalFY}</p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={handleSync} disabled={syncing} data-testid="crm-sync-btn" className="bg-slate-900 hover:bg-slate-800">
            {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing…</> : <><RefreshCw className="w-4 h-4 mr-2" />Sync CRM</>}
          </Button>
        )}
      </div>

      {/* KPI cards */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="crm-kpis">
          <Kpi icon={<Database className="w-4 h-4" />} label="Projects" value={s.count} tone="slate" />
          <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Revenue (excl GST)" value={fmt(s.revenue_excl_gst)} tone="indigo" money />
          <Kpi icon={<Wallet className="w-4 h-4" />} label="Total Costs" value={fmt(s.total_costs)} tone="rose" money />
          <Kpi icon={<PiggyBank className="w-4 h-4" />} label="Net Profit (W/O GST)" value={fmt(s.net_profit_wo_gst)} tone="emerald" money />
          <Kpi icon={<Wallet className="w-4 h-4" />} label="Payment Received" value={fmt(s.total_payment_made)} tone="teal" money />
          <Kpi icon={<Clock className="w-4 h-4" />} label="Pending Payment" value={fmt(s.pending_payment)} tone="amber" money />
        </div>
      )}

      {/* Per-type split */}
      {s && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="crm-type-split">
          {['Training', 'Venue', 'Consultancy'].map((t) => (
            <Card key={t} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${TYPE_BAR[t]}`} />
                  <span className="font-semibold text-slate-800">{t}</span>
                  <Badge variant="outline" className="ml-auto text-xs">{s.by_type[t].count} projects</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><p className="text-slate-400">Revenue</p><p className="font-medium tabular-nums">{fmt(s.by_type[t].revenue_excl_gst)}</p></div>
                  <div><p className="text-slate-400">Costs</p><p className="font-medium tabular-nums">{fmt(s.by_type[t].total_costs)}</p></div>
                  <div><p className="text-slate-400">Net Profit</p><p className="font-medium tabular-nums">{fmt(s.by_type[t].net_profit_wo_gst)}</p></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name or CRM ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="crm-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]" data-testid="crm-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="Corporate Training">Corporate Training</SelectItem>
            <SelectItem value="Venue">Venue</SelectItem>
            <SelectItem value="Consultancy">Consultancy</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
        ) : (
          <Table data-testid="crm-projects-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>CRM ID</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>PO Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Revenue (excl GST)</TableHead>
                <TableHead className="text-right">Costs</TableHead>
                <TableHead className="text-right">Net Profit</TableHead>
                <TableHead className="text-right">Pending</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!data?.projects || data.projects.length === 0) && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10">No CRM projects for this FY</TableCell></TableRow>
              )}
              {data?.projects?.map((p, i) => (
                <TableRow key={p.crm_project_id || i} className="cursor-pointer hover:bg-slate-50" onClick={() => p.crm_project_id && setDetailId(p.crm_project_id)} data-testid={`crm-row-${p.crm_project_id || i}`}>
                  <TableCell className="font-mono text-xs">{p.crm_project_id || '—'}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{p.training_name || '—'}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-xs"><span className={`w-2 h-2 rounded-full ${TYPE_BAR[p.mapped_type]}`} />{p.type_of_project}</span></TableCell>
                  <TableCell className="text-xs">{p.po_date || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{p.status || '—'}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(p.amount_without_gst)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(p.total_costs)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(p.net_profit_wo_gst)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(p.pending_payment)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <ProjectDetailModal {...{ crmProjectId: detailId, getAuthHeaders, formatCurrency: fmt, open: !!detailId, onClose: () => setDetailId(null) }} />
    </div>
  );
};

const Kpi = ({ icon, label, value, tone, money }) => {
  const tones = {
    slate: 'text-slate-700 bg-slate-100', indigo: 'text-indigo-700 bg-indigo-100',
    rose: 'text-rose-700 bg-rose-100', emerald: 'text-emerald-700 bg-emerald-100',
    teal: 'text-teal-700 bg-teal-100', amber: 'text-amber-700 bg-amber-100',
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md ${tones[tone]}`}>{icon}{label}</div>
        <p className={`mt-2 font-bold tabular-nums text-slate-900 ${money ? 'text-lg' : 'text-2xl'}`}>{value}</p>
      </CardContent>
    </Card>
  );
};

export default CrmProjectsPage;
